import { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "src/components/common/Logo";

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.classList.add("legal-page-route");
    document.body.classList.add("legal-page-route");

    return () => {
      document.documentElement.classList.remove("legal-page-route");
      document.body.classList.remove("legal-page-route");
    };
  }, []);

  return (
    <div className="min-h-screen w-full bg-white text-[#111827]">
      <header className="border-b border-[#e5e7eb] bg-white">
        <div className="mx-auto flex max-w-screen-lg items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold text-[#111827]">
            <Logo />
            <span>EatsBits</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-[#FD7657] hover:underline">
            Owner Login
          </Link>
        </div>
      </header>

      <main className="w-full px-4 py-6 sm:px-6 md:px-10">
        <div className="container mx-auto max-w-screen-lg p-4 sm:p-6 lg:p-8">
          <h1 className="mb-6 text-center text-2xl font-bold sm:text-3xl">
            Terms of Service
          </h1>
          <p className="mb-8 text-center text-sm text-gray-500">
            Effective Date: March 23, 2026
          </p>

          <section className="mb-6 mt-8">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              1. Acceptance of Terms
            </h2>
            <p>
              By accessing or using EatsBits, including restaurant management
              tools, online ordering, point-of-sale or operational features,
              enterprise lunch ordering, group meal programs, community meal
              subscriptions, and related services, you agree to these Terms of
              Service and our{" "}
              <a href="/privacy/" className="text-blue-500 underline">
                Privacy Policy
              </a>
              . If you do not agree, please do not use the platform.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              2. Use of the Platform
            </h2>
            <p>
              EatsBits provides technology for restaurants, organizations, and
              diners, including tools for restaurant operations, menu and order
              management, online ordering, enterprise meal programs, and
              community meal subscriptions. You agree to use the platform
              lawfully and not to misuse, disrupt, or attempt unauthorized
              access to any part of the service.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              3. Accounts and User Responsibilities
            </h2>
            <ul className="list-inside list-disc space-y-2">
              <li>
                You are responsible for maintaining the confidentiality of your
                account credentials.
              </li>
              <li>
                You are responsible for ensuring the information you provide is
                accurate and up to date.
              </li>
              <li>
                You must notify us promptly if you suspect unauthorized use of
                your account.
              </li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              4. Orders, Plans, Pricing, and Payments
            </h2>
            <p>
              When you place an order, manage a restaurant account, subscribe to
              a plan, participate in an enterprise lunch program, or enroll in a
              community meal subscription, you agree to pay all applicable
              charges displayed or otherwise agreed to, including menu prices,
              subscription fees, service fees, taxes, delivery or fulfillment
              charges, and other applicable amounts.
            </p>
            <p className="mt-3">
              Restaurants, organizations, and EatsBits may update menu
              availability, plan features, program eligibility, ordering
              windows, fulfillment options, and pricing from time to time.
              Estimated preparation, pickup, delivery, or fulfillment times are
              not guaranteed. Delays may occur due to restaurant capacity,
              traffic, weather, program volume, or other factors outside of our
              control.
            </p>
            <p className="mt-3">
              By providing your phone number during checkout, account setup,
              restaurant onboarding, enterprise lunch participation, or
              community meal subscription signup, you consent to receive
              transactional SMS messages as described in Section 13.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              5. Cancellations and Refunds
            </h2>
            <p>
              Cancellation and refund eligibility depends on restaurant
              policies, meal program rules, subscription terms, order status,
              fulfillment timing, and applicable law. We may assist with refund
              requests but cannot guarantee refunds for all cases.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              6. Restaurant, Organization, and Program Content
            </h2>
            <p>
              EatsBits acts as a technology platform for restaurant partners,
              organizations, meal program sponsors, and users. Restaurants are
              responsible for food preparation, quality, menu accuracy, and
              fulfillment of orders unless otherwise expressly agreed. EatsBits
              does not prepare food and is not responsible for the actions or
              omissions of independent restaurant partners.
            </p>
            <p className="mt-3">
              Restaurant descriptions, menus, item photos, availability,
              enterprise lunch details, group meal rules, community meal
              subscription details, and other program information may be provided
              by restaurant partners, organizations, sponsors, users, or
              third-party sources. While we try to keep information accurate, we
              cannot guarantee it is always complete or current.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              7. Intellectual Property
            </h2>
            <p>
              All platform content, branding, logos, and software are owned by
              EatsBits or its licensors and are protected by applicable
              intellectual property laws. You may not copy, modify, distribute,
              or reverse engineer platform materials except as permitted by law.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              8. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by law, EatsBits is not liable for
              indirect, incidental, special, consequential, or punitive damages,
              including loss of profits, data, or goodwill arising from your use
              of the platform.
            </p>
            <p className="mt-3">
              To the fullest extent permitted by law, EatsBits&apos; total
              liability arising out of or related to your use of the platform
              shall not exceed the amount paid by you for the specific order,
              plan, subscription period, or meal program transaction giving rise
              to the claim.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              9. Food Safety and Allergy Notice
            </h2>
            <p>
              If you have food allergies or dietary restrictions, please contact
              the restaurant directly before placing an order or participating
              in a meal program. EatsBits is not responsible for any adverse
              reactions to food items ordered, provided, or made available
              through the platform.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              10. Suspension or Termination
            </h2>
            <p>
              We may suspend or terminate access to the platform if we reasonably
              believe these Terms have been violated, fraudulent activity has
              occurred, or continued access may harm users, partners, or the
              platform.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              11. Governing Law
            </h2>
            <p>
              These Terms are governed by and construed in accordance with the
              laws of the State of Texas, without regard to its conflict of law
              principles.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              12. Changes to These Terms
            </h2>
            <p>
              We may update these Terms from time to time. Updated Terms will be
              posted on this page with an effective date. Continued use of the
              platform after updates means you accept the revised Terms.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              13. SMS Messaging Terms
            </h2>
            <p className="mb-2">
              By providing your phone number during checkout, account setup,
              restaurant onboarding, enterprise lunch participation, community
              meal subscription signup, or other platform use, you agree to
              receive transactional SMS messages related to your account, order,
              restaurant operation, meal program, or subscription. These messages
              may include order confirmations, order status updates, pickup
              notifications, delivery or fulfillment notices, account notices,
              and program updates.
            </p>
            <ul className="mb-2 list-inside list-disc space-y-2">
              <li>Message frequency varies based on your activity.</li>
              <li>Message and data rates may apply.</li>
              <li>You may opt out at any time by replying STOP.</li>
              <li>You may reply HELP for assistance.</li>
            </ul>
            <p>
              We do not send marketing or promotional messages without your
              explicit consent. For more information about how we collect and
              use personal information, please review our{" "}
              <a href="/privacy/" className="text-blue-500 underline">
                Privacy Policy
              </a>
              .
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              14. Contact Information
            </h2>
            <p>
              For questions regarding these Terms of Service, please contact us
              at:
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:support@eatsbits.com"
                className="text-blue-500 underline"
              >
                support@eatsbits.com
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
